
import { Icon } from '@/shared/components/Icon/Icon'
import { MosaicDragItem, MosaicDragType } from '@/shared/components/Mosaic'
import { pxToRem } from '@/shared/styles/themes'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { styled, Typography } from '@mui/material'
import { ComponentProps } from 'react'
import { useDrop } from 'react-dnd'

export type MosaicLayoutZeroStateProps = ComponentProps<typeof StyledZeroStateContainer> & {
  icon: IconDefinition
  onNodeCreate: (item: MosaicDragItem) => void
  title?: string
  subtitle?: string
}

export const MosaicLayoutZeroState: React.FC<MosaicLayoutZeroStateProps> = ({ icon, title, subtitle, onNodeCreate, ...props }) => {
  const [{ isOver }, dropRef] = useDrop({
    accept: [MosaicDragType.WIDGET_BUTTON, MosaicDragType.TREE_ITEM],
    drop: (item: MosaicDragItem) => {
      if (!item.widgetType) return

      if (onNodeCreate) {
        onNodeCreate(item)
      }

      return { isNewWindow: true }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  })

  return (
    <StyledZeroStateContainer ref={dropRef} {...props}>
      <StyledIcon icon={icon} />
      <StyledCallToAction variant="body1">{title ?? 'Ready to optimise your runs'}</StyledCallToAction>
      <StyledActionTypography variant="body1">{subtitle ?? 'Add widgets to grid here by dragging them from your dock on the top, or add vehicle shifts from your scenarios on the left'}</StyledActionTypography>
      <StyledDropPlaceholder $isOver={isOver} />
    </StyledZeroStateContainer>
  )
}

const StyledDropPlaceholder = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isOver',
}) <{ $isOver: boolean }>`
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.palette.custom.widgetDropTarget.backgroundColor};
  border-radius: ${pxToRem(8)};
  border: ${({ theme }) => theme.palette.custom.widgetDropTarget.border};
  opacity: ${({ $isOver }) => ($isOver ? 1 : 0)};
  transition: opacity 0.1s;
  padding: ${props => props.theme.typography.pxToRem(8)};
`

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.pxToRem(64)};
`
const StyledZeroStateContainer = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.typography.pxToRem(8)};
  border: 1px dashed ${({ theme }) => theme.palette.divider};
  margin: ${({ theme }) => theme.typography.pxToRem(8)} 0 ${({ theme }) => theme.typography.pxToRem(8)} ${({ theme }) => theme.typography.pxToRem(8)};
`

const StyledCallToAction = styled(Typography)`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-top: ${({ theme }) => theme.typography.pxToRem(20)};
  font-size: ${({ theme }) => theme.typography.pxToRem(20)};
  text-align: center;
`

const StyledActionTypography = styled(Typography)`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.secondary};
  max-width: ${({ theme }) => theme.typography.pxToRem(520)};
  line-height: ${({ theme }) => theme.typography.pxToRem(20)};
  margin-top: ${({ theme }) => theme.typography.pxToRem(8)};
  font-size: ${({ theme }) => theme.typography.pxToRem(14)};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.typography.pxToRem(256)};
`